import { FC } from 'react';

import campaignsDashboardAllFiltersModalFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/campaigns_dashboard/campaigns_dashboard_all_filters_modal_form_submit';
import { ActionBar, BottomSheet, Button, Modal, NavBar, Text, useBreakpoint } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { AllFiltersModalContent } from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/components/AllFiltersModal/AllFiltersModalContent';
import { useApplyCampaignsFilters } from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/useApplyCampaignsFilters';
import { getAppliedCampaignsFiltersTotalCount } from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/utils';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { INITIAL_APPLIED_CAMPAIGNS_FILTERS_STATE, setCampaignsFiltersDraft } from 'store/models/campaignsFilters';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'CampaignsFiltersPanel.AllFiltersModal.title',
    applyButton: 'CampaignsFiltersPanel.AllFiltersModal.applyButton',
    cancelButton: 'CampaignsFiltersPanel.AllFiltersModal.cancelButton',
    resetButton: 'CampaignsFiltersPanel.AllFiltersModal.resetButton',
};

interface AllFiltersModalProps {
    visible: boolean;
    onClose: () => void;
}

export const AllFiltersModal: FC<AllFiltersModalProps> = ({ visible, onClose }) => {
    const appliedCampaignsFiltersTotalCount = useSelector(
        (state) => state.campaignsFilters.appliedCampaignsFiltersTotalCount
    );
    const appliedCampaignsFilters = useSelector((state) => state.campaignsFilters.appliedCampaignsFilters);
    const campaignsFiltersDraft = useSelector((state) => state.campaignsFilters.campaignsFiltersDraft);
    const isCampaignsLoading = useSelector((state) => state.campaignsDashboard.isCampaignsLoading);

    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();

    const { applyCampaignsFilters } = useApplyCampaignsFilters();

    const resetFiltersHandler = () => {
        applyCampaignsFilters({
            filters: INITIAL_APPLIED_CAMPAIGNS_FILTERS_STATE,
            count: 0,
            onSuccess: () => {
                campaignsDashboardAllFiltersModalFormSubmit();
            },
            onError: (error) => {
                campaignsDashboardAllFiltersModalFormSubmit({ error: JSON.stringify(error) });
            },
        });
        dispatch(setCampaignsFiltersDraft({ ...INITIAL_APPLIED_CAMPAIGNS_FILTERS_STATE }));

        onClose();
    };

    const onAfterExitHandler = () => {
        dispatch(setCampaignsFiltersDraft({ ...appliedCampaignsFilters }));
    };

    const applyFiltersHandler = () => {
        applyCampaignsFilters({
            filters: campaignsFiltersDraft,
            count: getAppliedCampaignsFiltersTotalCount(campaignsFiltersDraft),
            onSuccess: () => {
                campaignsDashboardAllFiltersModalFormSubmit({ filters: JSON.stringify(campaignsFiltersDraft) });
            },
            onError: (error) => {
                campaignsDashboardAllFiltersModalFormSubmit({
                    filters: JSON.stringify(campaignsFiltersDraft),
                    error: JSON.stringify(error),
                });
            },
        });

        onClose();
    };

    const footer = (
        <ActionBar
            secondaryActions={
                appliedCampaignsFiltersTotalCount > 0 && (
                    <Button
                        mode="tertiary"
                        style="accent"
                        onClick={resetFiltersHandler}
                        data-qa="all-filters-modal-reset-button"
                    >
                        {trls(TrlKeys.resetButton)}
                    </Button>
                )
            }
            primaryActions={
                <Button
                    mode="primary"
                    style="accent"
                    onClick={applyFiltersHandler}
                    data-qa="all-filters-modal-apply-button"
                    disabled={isCampaignsLoading}
                >
                    {trls(TrlKeys.applyButton)}
                </Button>
            }
        />
    );

    if (isMobile) {
        return (
            <BottomSheet
                visible={visible}
                onClose={onClose}
                onAfterExit={onAfterExitHandler}
                height="full-screen"
                footer={footer}
                header={
                    <NavBar>
                        <NavBar.Pane>
                            <NavBar.Actions
                                right={<CrossOutlinedSize24 padding={8} initialColor="secondary" onClick={onClose} />}
                            >
                                <NavBar.TitleContainer
                                    title={<Text typography="title-5-semibold">{trls(TrlKeys.title)}</Text>}
                                />
                            </NavBar.Actions>
                        </NavBar.Pane>
                    </NavBar>
                }
                data-qa="campaigns-all-filters-bottom-sheet"
            >
                <AllFiltersModalContent />
            </BottomSheet>
        );
    }

    return (
        <Modal
            size="small"
            visible={visible}
            onClose={onClose}
            onAfterExit={onAfterExitHandler}
            header={
                <MagritteModalHeader
                    title={trls(TrlKeys.title)}
                    actions={<CrossOutlinedSize24 initialColor="secondary" onClick={onClose} />}
                />
            }
            footer={footer}
            data-qa="campaigns-all-filters-modal"
        >
            <AllFiltersModalContent />
        </Modal>
    );
};
